import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  EpActivityService,
  EpActivityType,
  EpApplicationActivityService,
  EpApplicationService,
  EpDialogAction,
  EpDialogButtons,
  EpDialogService,
  EpErpSessionService,
  EpIdPAuthService,
  EpLocalStorageService,
  EpLoginService,
  EpMenuBarService,
  EpPromiseType,
  EpShellService,
  IEpAccountPanelList,
  IEpActivityArgs,
  IEpActivityHandler,
  IEpActivityResult,
  IEpApplicationActivityData,
  IEpDialogOptions,
  IEpDialogResult,
  IEpDropdownModel,
  IEpFavoritesPanelList,
  IEpLoginModel,
  IEpMenuItem,
  IEpMenuPanelKeys,
  IEpMultiLevelMenuModel,
  IEpPanelList,
  IEpPanelListItem,
  IEpShellAccountPanelModel,
  IEpSlidingPanelActivityData
} from '@epicor/ux-platform';
import { Router } from '@angular/router';
import { HelpPageService } from './services/help-page.service';
import { ServiceType } from './models/service-type';
import { forkJoin, from, Observable, of } from 'rxjs';
import { HelpType } from './models/help-type';
import { RestVersions } from './models/rest-versions';
import { AppLocalizationStrings } from './app.localization.strings';
import { map, switchMap, tap } from 'rxjs/operators';
import { appsSubPath, restHelpCurCompStorageKey, restHelpVersionStorageKey } from './app.constants';
import { UserManager } from 'oidc-client';
import { MenuFavoriteService } from './services/menu-favorite.service';
import { IMenuFavoritesSet } from './models/menu-favorites.interface';
import { ContextService } from './services/context.service';

const menuSetId = 'RestHelpMenu';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  @ViewChild('dialogTemplateFav', { static: true }) dialogTemplateFav: TemplateRef<any>;
  @ViewChild('editFavFolderNameTemplate', { static: true }) editFavFolderNameTemplate: TemplateRef<any>;
  appStrings = AppLocalizationStrings;

  companiesDropDownList: IEpDropdownModel = {
    data: [],
    epOnValueChange: item => {
      if (item.event) {
        this.epLocalStorageService.set(restHelpCurCompStorageKey, item.event);
      }
    }
  };

  favModel: IEpFavoritesPanelList = {
    lists: [],
    epOnFavoriteItemAction: (item, folder) => {
      this.editFromFavorites(item);
    },
    epOnFavoriteFolderAction: folder => {
      this.editFolder(folder);
    },

    menuSetId: menuSetId
  };

  data = {
    name: '',
    isNewFolder: false,
    newFolder: '',
    allowBlankFolder: false,
    folder: '',
    folders: [],
    folderSelect: args => {}
  };

  defaultItem = { caption: this.appStrings.selectFolder, id: '' };

  menuModel: IEpMultiLevelMenuModel = {
    items: [
      {
        id: ServiceType.BO,
        caption: this.appStrings.menuBOServices,
        icon: 'mdi mdi-cube-outline mdi-18px',
        folder: true
      },
      {
        id: ServiceType.Lib,
        caption: this.appStrings.menuLibServices,
        icon: 'mdi mdi-cube-outline mdi-18px',
        folder: true
      },
      {
        id: ServiceType.Proc,
        caption: this.appStrings.menuProcServices,
        icon: 'mdi mdi-cube-outline mdi-18px',
        folder: true
      },
      {
        id: ServiceType.Rpt,
        caption: this.appStrings.menuRptServices,
        icon: 'mdi mdi-cube-outline mdi-18px',
        folder: true
      },
      {
        id: ServiceType.UD,
        caption: this.appStrings.menuUDServices,
        icon: 'mdi mdi-cube-outline mdi-18px',
        folder: true
      },
      {
        id: 'BAQ',
        caption: this.appStrings.menuBAQ,
        action: () => {
          this.selectBaq();
        }
      },
      {
        id: 'Functions',
        caption: this.appStrings.menuFunctions,
        action: () => {
          this.selectEfx();
        }
      }
    ],
    disableFavoritesDialog: false,
    menuSetId: menuSetId,
    rootMenuId: ''
  };

  userDetail: IEpAccountPanelList = {
    enableTouchMode: false,
    enableDarkMode: false,
    enableMoreSettings: false,
    enableLogOut: true,
    languageNameList: undefined,
    siteNameList: undefined,
    wsNameList: undefined,
    userNameList: undefined,
    countryNameList: undefined,
    showSettings: false,
    enableHotkeysPanel: false,
    userProfile: {},
    companyNameList: this.companiesDropDownList,

    epOnLogOut: item => {
      this.epErpSessionService.epToken.logOut();
    }
  };

  companyList: Array<string>;

  constructor(
    private router: Router,
    private epMenuBarService: EpMenuBarService,
    private epShellService: EpShellService,
    private epAppService: EpApplicationService,
    private helpPageService: HelpPageService,
    private epActivityService: EpActivityService,
    private epLocalStorageService: EpLocalStorageService,
    private epLoginService: EpLoginService,
    private epErpSessionService: EpErpSessionService,
    private epAppActivity: EpApplicationActivityService,
    private epIdPAuthService: EpIdPAuthService,
    private favService: MenuFavoriteService,
    private epDialog: EpDialogService,
    private contextService: ContextService
  ) {
    this.setupPreApplicationInit();
  }

  ngOnInit() {
    this.registerSlidingPanelHandler();
    this.epShellService.setTitle(this.appStrings.home);

    this.prepareMenuLoad();

    const hideMenuItems: IEpMenuPanelKeys = {
      primary: {
        history: true,
        add: true,
        videos: true,
        charts: true,
        monitor: true,
        notes: true,
        search: true
      },
      secondary: {
        account: false,
        collaborate: true,
        help: true,
        notifications: true
      }
    };

    this.epShellService.manageMenuBarItems(hideMenuItems, 'hidden');

    this.initUserAccount();
  }

  prepareMenuLoad() {
    this.epMenuBarService.menu.onLoad = panelModel => {
      this.epAppService
        .onConfigured() // we call it inside menu onLoad to be sure it only happens after complete login
        .pipe(
          switchMap(_ => this.getCompanies()),
          switchMap(_ => this.initMenu()),
          switchMap(_ => this.contextService.initializeAfterLogin()),
        )
        .subscribe();
    };
  }

  initMenu(): Observable<void> {
    return forkJoin([
      this.addMenuItems(ServiceType.BO),
      this.addMenuItems(ServiceType.Lib),
      this.addMenuItems(ServiceType.Rpt),
      this.addMenuItems(ServiceType.Proc),
      this.addMenuItems(ServiceType.UD)
    ]).pipe(
      map(menus => {
        this.menuModel.items = this.menuModel.items.concat(...menus);

        this.menuModel.onFavoriteAction = item => this.editFromMenu(item, this.menuModel.items);

        this.epShellService.loadMenu(this.menuModel);
        this.loadFavoritesMenu();
        this.epShellService.setRecentPanelvisibility(true); // hide recent

        // show UD only if found
        this.checkUdMenu();
      })
    );
  }

  checkUdMenu() {
    if (!this.menuModel.items.find(mi => mi.parentId === ServiceType.UD)) {
      const udMenuInd = this.menuModel.items.findIndex(mi => mi.id === ServiceType.UD);
      if (udMenuInd >= 0) {
        // hidden did not work, so just delete menu from list
        this.menuModel.items.splice(udMenuInd, 1);
      }
    }
  }

  loadFavoritesMenu() {
    this.favModel.lists = this.toPanelList(this.favService.getMenuSet(menuSetId));
    this.epShellService.loadFavorites(this.favModel);
  }

  toPanelList(favList: IMenuFavoritesSet): IEpPanelList[] {
    const favorites = <IEpPanelList[]>[];
    if (favList?.items?.length) {
      for (const fav of favList.items.filter(i => i.isFolder)) {
        favorites.push({
          id: fav.id,
          category: fav.caption,
          items: this.getFavFolderItems(fav.id, favList),
          epOnListItemClick: e => {
            this.favClick(e.event);
          }
        });
      }
    }
    return favorites;
  }

  getFavFolderItems(id: string, favList: IMenuFavoritesSet): IEpPanelListItem[] {
    const items = <IEpPanelListItem[]>[];
    for (const item of favList.items.filter(i => !i.isFolder && i.parentId === id)) {
      items.push({
        id: item.id,
        text: item.caption,
        command: item.path
      });
    }
    return items;
  }

  favClick(event: any) {
    if (event.id) {
      const menuItem = this.menuModel.items.find(m => !m.folder && m.id === event.id);
      if (menuItem && menuItem.action) {
        menuItem.action(menuItem);
      }
    }
  }

  editFromMenu(item: IEpMenuItem, menuItems: IEpMenuItem[]): EpPromiseType<boolean> {
    from(
      this.favService.editDialogForMenuItem(menuSetId, item, this.getItemPath(item), this.data, this.dialogTemplateFav)
    ).subscribe(dlgResult => {
      if (dlgResult) {
        this.loadFavoritesMenu();
      }
    });

    return true;
  }

  editFromFavorites(panelItem: IEpPanelListItem) {
    const favItem = this.favService.getMenuSet(menuSetId).items.find(f => f.id === panelItem.id);

    if (favItem) {
      from(this.favService.editDialogForFavoriteItem(menuSetId, favItem, this.data, this.dialogTemplateFav)).subscribe(
        dlgResult => {
          if (!dlgResult) {
            const menuObject = this.menuModel.items.find(i => i.id === panelItem.id);
            menuObject.favorite = false;
          }
          this.loadFavoritesMenu();
        }
      );
    }
  }

  editFolder(folder: IEpPanelList) {
    from(this.editOrDeleteFolder(folder)).subscribe(dlgResult => {
      this.loadFavoritesMenu();
    });
  }

  editOrDeleteFolder(folder: IEpPanelList): Promise<boolean> {
    const lastName = folder.category;
    this.data.folder = folder.category;

    const dlg = this.epDialog.info(<IEpDialogOptions>{
      title: this.appStrings.folderDialogTitle,
      icon: 'mdi mdi-pencil',
      template: this.editFavFolderNameTemplate,
      buttons: [
        { id: 'delete', labelText: this.appStrings.deleteBtn },
        { id: 'done', labelText: this.appStrings.doneBtn }
      ],
      data: this.data,
      stackable: true,
      fnValidateAction: (action: EpDialogAction) => {
        if (action.id === 'delete') {
          this.epDialog
            .confirm(<IEpDialogOptions>{
              message: this.appStrings.deleteFolder,
              description: this.appStrings.favoriteMenusRemoved
            })
            .promise.then((result: IEpDialogResult) => {
              if (result.action.id === 'default') {
                this.favService.removeFolder(menuSetId, folder.id);
                this.epDialog.close();
              }
            });
          return false;
        } else if (action.id === 'done') {
          if (this.data.folder !== lastName) {
            this.favService.renameFolder(menuSetId, folder.id, this.data.folder);
            return true;
          } else {
            this.epDialog.close();
          }
        }
      }
    });

    return dlg.promise.then(v => v.action.id === 'done');
  }

  private getItemPath(item: IEpMenuItem): string {
    let ret = '';
    let cur = item;
    const rootId = '';
    while (true) {
      const parent = this.menuModel.items.find(m => m.id === cur.parentId);
      if (!parent || (parent.id || '') === rootId) {
        break;
      }
      const sep = ret ? ' / ' : '';
      ret = parent.caption + sep + ret;
      cur = parent;
    }
    return ret;
  }

  /** Event when user presses folder icon - toggles between existing and new folder options   */
  addFolder(mode: number) {
    this.data.isNewFolder = mode === 1;
    if (!this.data.isNewFolder) {
      this.data.newFolder = '';
    }
  }

  private addMenuItems(serviceType: ServiceType): Observable<IEpMenuItem[]> {
    return this.helpPageService.getServices(serviceType).pipe(
      map(services => {
        return services.map<IEpMenuItem>(
          svc =>
            <IEpMenuItem>{
              id: svc,
              parentId: serviceType,
              caption: svc,
              folder: false,
              description: svc,
              action: () => {
                this.selectMethods(svc, serviceType);
              }
            }
        );
      })
    );
  }

  private selectMethods(serviceName: string, serviceType: ServiceType) {
    const defaultHelpType = HelpType.OData;
    this.router.navigate(['select-service', this.getVersionToUse(), defaultHelpType, serviceName, '']);
  }

  private selectBaq() {
    this.router.navigate(['select-baq', this.getVersionToUse(), this.getCompanyToUse(), '']);
  }

  private selectEfx() {
    this.router.navigate(['select-efx', RestVersions.V2, this.getCompanyToUse(), '']);
  }

  getVersionToUse(): RestVersions {
    const lastVersion = this.epLocalStorageService.get(restHelpVersionStorageKey);
    if (lastVersion !== RestVersions.V1) {
      return RestVersions.V2;
    }
    return RestVersions.V1;
  }

  getCompanyToUse(): string {
    const lastCompany = this.epLocalStorageService.get(restHelpCurCompStorageKey);

    if (this.companyList && this.companyList.length) {
      if (lastCompany && this.companyList.includes(lastCompany)) {
        return lastCompany;
      }
      return this.companyList[0];
    } else {
      return lastCompany ?? '';
    }
  }

  private initUserAccount() {
    this.epMenuBarService.account.onLoad = (panelModel: IEpShellAccountPanelModel) => {
      this.epShellService.loadUserDetails(this.userDetail);
      if (this.userDetail.companyNameList.data.length === 0) {
        this.getCompanies().subscribe(compList => {
          this.epShellService.accountPanelModelInstance().companyNameList.data = compList;
          const selected = this.epLocalStorageService.get(restHelpCurCompStorageKey);
          this.epShellService.accountPanelModelInstance().companyNameList.value = selected ? selected : compList[0];
        });
      }
    };
  }

  getCompanies(): Observable<string[]> {
    if (this.companyList) {
      return of(this.companyList);
    }
    return this.helpPageService.getCompanies().pipe(tap(list => (this.companyList = list)));
  }

  private registerSlidingPanelHandler() {
    // make sliding panel work again
    const handler: IEpActivityHandler<IEpSlidingPanelActivityData> = {
      id: 'SetHeaderComponent',
      types: ['EpSlidingPanelActivity-onBeforeActivity'],

      handleActivity(data: IEpActivityArgs<IEpSlidingPanelActivityData>): Observable<IEpActivityResult<any>> {
        const res: IEpActivityResult<any> = {
          result: data.args,
          type: 'SetHeaderComponent'
        };
        return of(res);
      }
    };
    this.epActivityService.registerHandler(handler);
  }

  private setupPreApplicationInit() {
    const handler = (args: IEpApplicationActivityData): EpPromiseType<IEpApplicationActivityData> => {
      // change storage key
      this.epLocalStorageService.settingsId = this.createStorageKey(window.location.pathname);

      this.adjustIdpUserManager();

      // set app server url to avoid setup.
      const appUrl = this.getAppServerUrl(window.location);

      if (appUrl.length) {
        this.epErpSessionService.restoreConnection();
        const model = <IEpLoginModel>this.epLoginService.getModel();
        model.useStoredServerUrl = false;
        model.serverUrl = appUrl;
        this.epErpSessionService.setConnection(model, false, true);

        // adjust Idp redirects from sysconfig.json
        if (this.epIdPAuthService.model) {
          this.epIdPAuthService.model.redirect_uri = appUrl + appsSubPath + 'resthelp/';
          this.epIdPAuthService.model.silent_redirect_uri = appUrl + appsSubPath + 'resthelp/silent-renew.html';
        }
      }
      return args;
    };

    this.epAppActivity.registerApplicationInitHandler(EpActivityType.onBeforeActivity, handler, '1');
  }

  createStorageKey(pathname: string): string {
    if (pathname.length > 1) {
      const path = pathname.toLowerCase();
      const index = path.indexOf(appsSubPath);
      if (index > 0) {
        return path.substring(0, index).replace('/', '');
      }
    }
    return '';
  }

  getAppServerUrl(loc: Location): string {
    let path = loc.pathname.toLowerCase();
    const index = path.indexOf(appsSubPath);
    if (index > 0) {
      path = path.substring(0, index);
      return loc.origin.toLowerCase() + path;
    } else {
      return '';
    }
  }

  private adjustIdpUserManager() {
    if (this.epIdPAuthService.userManager) {
      return;
    }

    // try to load idp data, with correct storage key, for now it is not loaded because of KNTC-6223
    // which is working with appData setting id only.
    // so I reload Idp config if it is any
    const idpConfig = this.epLocalStorageService.get('EpIdPConfig');

    // if idp config is found, repeat same stuff as in constructor EpIdPAuthService.
    if (idpConfig) {
      this.epIdPAuthService.userManager = new UserManager(idpConfig);

      // Loads the user object for the currently authenticated user.
      this.epIdPAuthService.userManager.getUser().then(user => {
        if (user) {
          // @ts-ignore
          this.epIdPAuthService.user = user; // need to set user even if it is not public
        }
      });
      this.epIdPAuthService.registerEvents();
    }
  }
}
